import { forwardRef, useMemo } from 'react';
import { cva, cx, VariantProps } from 'class-variance-authority';
import { BaseTypeMap, Component, ComponentProps } from './types/Component';
import { Typography, TypographyProps } from './Typography';
import { pipe } from 'fp-ts/lib/function';
import { chain, fromPredicate, getOrElse, map } from 'fp-ts/lib/Option';

export const label = cva([], {
  variants: {
    floating: {
      true: [
        'absolute',
        'ease-in-out',
        'duration-100',
        'left-0',
        'peer-focus:top-0',
        'peer-focus:text-sm',
      ],
      false: [],
    },
    rise: {
      true: [],
      false: [],
    },
    disabled: {
      true: ['text-neutral-500'],
      false: ['text-inherit'],
    },
  },
  compoundVariants: [
    {
      floating: true,
      rise: true,
      className: ['top-0'],
    },
    {
      floating: true,
      rise: false,
      className: ['top-6'],
    },
  ],
});

type LabelTypeMap = BaseTypeMap<
  'label',
  Pick<TypographyProps, 'size' | 'weight' | 'color'> &
    VariantProps<typeof label> & { disabled?: boolean }
>;

export type LabelProps = ComponentProps<LabelTypeMap>;

export const Label: Component<LabelTypeMap> = forwardRef(function Label(
  {
    children,
    className,
    as = 'label',
    floating,
    rise,
    size = 'md',
    disabled,
    ...rest
  },
  ref
) {
  const s = useMemo(
    () =>
      pipe(
        floating,
        fromPredicate((x) => !!x),
        chain(() =>
          pipe(
            rise,
            fromPredicate((x) => !!x)
          )
        ),
        map(() => 'sm' as typeof size),
        getOrElse(() => size)
      ),
    [size, floating, rise]
  );

  return (
    <Typography
      className={cx(label({ floating, rise, disabled }), className)}
      ref={ref}
      {...rest}
      intent="label"
      size="lg"
      as={as}
    >
      {children}
    </Typography>
  );
});
